import { useState, useEffect, useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  Trash2,
  RotateCcw,
  AlertTriangle,
  Clock,
  CreditCard,
  Receipt,
  Users,
  Target,
  Wallet,
  Tag,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  Info,
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import Button from '../components/common/Button';
import ScrollableTabs from '../components/common/ScrollableTabs';
import * as archivesService from '../services/archives';
import { useBankAccountStore } from '../store/bankAccountStore';
import type {
  ArchivesData,
  ArchivedBankAccount,
  ArchivedPersonalOperation,
  ArchivedSharedOperation,
  ArchivedSharedBankAccount,
  ArchivedBudget,
  ArchivedEnvelope,
  ArchivedCategory,
  ArchivedPartner2IncomeChange,
} from '../types';
import { useAuthStore } from '../store/authStore';

type TabType =
  | 'bank-accounts'
  | 'personal-operations'
  | 'shared-operations'
  | 'shared-bank-accounts'
  | 'budgets'
  | 'envelopes'
  | 'categories'
  | 'partner2-incomes';

type ArchivedItem =
  | ArchivedBankAccount
  | ArchivedPersonalOperation
  | ArchivedSharedOperation
  | ArchivedSharedBankAccount
  | ArchivedBudget
  | ArchivedEnvelope
  | ArchivedCategory
  | ArchivedPartner2IncomeChange;

// Helper pour convertir kebab-case en PascalCase
function toPascalCase(str: string): string {
  return str
    .split('-')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');
}

// Composant pour rendre une icône Lucide dynamiquement
function DynamicIcon({ name, className }: { name: string; className?: string }) {
  const pascalName = toPascalCase(name);
  const IconComponent = (LucideIcons as unknown as Record<string, React.ComponentType<{ className?: string }>>)[pascalName];

  if (!IconComponent) {
    return null;
  }

  return <IconComponent className={className} />;
}

export default function Archives() {
  const { t, i18n } = useTranslation('archives');
  const { t: tc } = useTranslation('common');
  const { user } = useAuthStore();
  const { fetchBankAccounts, fetchSharedBankAccounts } = useBankAccountStore();
  const [data, setData] = useState<ArchivesData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('bank-accounts');
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning'; text: string } | null>(null);

  // Modales
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<{
    type: TabType;
    item: ArchivedItem;
  } | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // États d'expansion des cartes de compte
  const [expandedAccounts, setExpandedAccounts] = useState<Set<string>>(new Set());

  const hasCouple = !!user?.coupleId;

  const fetchArchives = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await archivesService.getAllArchives();
      setData(result);
    } catch {
      setError(t('loadError'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchArchives();
  }, [fetchArchives]);

  const handleRestore = async () => {
    if (!selectedItem) return;
    setIsProcessing(true);
    setMessage(null);

    try {
      let result;
      switch (selectedItem.type) {
        case 'bank-accounts':
          result = await archivesService.restoreBankAccount(selectedItem.item.id);
          break;
        case 'personal-operations':
          result = await archivesService.restorePersonalOperation(selectedItem.item.id);
          break;
        case 'shared-operations':
          result = await archivesService.restoreSharedOperation(selectedItem.item.id);
          break;
        case 'shared-bank-accounts':
          result = await archivesService.restoreSharedBankAccount(selectedItem.item.id);
          break;
        case 'budgets':
          result = await archivesService.restoreBudget(selectedItem.item.id);
          break;
        case 'envelopes':
          result = await archivesService.restoreEnvelope(selectedItem.item.id);
          break;
        case 'categories':
          result = await archivesService.restoreCategory(selectedItem.item.id);
          break;
        case 'partner2-incomes':
          result = await archivesService.restorePartner2Income(selectedItem.item.id);
          break;
      }

      if (result.warnings && result.warnings.length > 0) {
        setMessage({
          type: 'warning',
          text: `${result.message} Attention : ${result.warnings.join(', ')}`,
        });
      } else {
        setMessage({ type: 'success', text: result.message });
      }

      setShowRestoreModal(false);
      setSelectedItem(null);
      fetchArchives();

      // Rafraîchir le store global des comptes pour mettre à jour la sidebar
      if (selectedItem.type === 'bank-accounts') {
        fetchBankAccounts();
      } else if (selectedItem.type === 'shared-bank-accounts') {
        fetchSharedBankAccounts();
      }
    } catch (err: any) {
      setMessage({
        type: 'error',
        text: err.response?.data?.message || t('restoreError'),
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePermanentDelete = async () => {
    if (!selectedItem) return;
    setIsProcessing(true);
    setMessage(null);

    try {
      switch (selectedItem.type) {
        case 'bank-accounts':
          await archivesService.permanentDeleteBankAccount(selectedItem.item.id);
          break;
        case 'personal-operations':
          await archivesService.permanentDeletePersonalOperation(selectedItem.item.id);
          break;
        case 'shared-operations':
          await archivesService.permanentDeleteSharedOperation(selectedItem.item.id);
          break;
        case 'shared-bank-accounts':
          await archivesService.permanentDeleteSharedBankAccount(selectedItem.item.id);
          break;
        case 'budgets':
          await archivesService.permanentDeleteBudget(selectedItem.item.id);
          break;
        case 'envelopes':
          await archivesService.permanentDeleteEnvelope(selectedItem.item.id);
          break;
        case 'categories':
          await archivesService.permanentDeleteCategory(selectedItem.item.id);
          break;
        case 'partner2-incomes':
          await archivesService.permanentDeletePartner2Income(selectedItem.item.id);
          break;
      }

      setMessage({ type: 'success', text: t('deletedPermanently') });
      setShowDeleteModal(false);
      setSelectedItem(null);
      fetchArchives();
    } catch (err: any) {
      setMessage({
        type: 'error',
        text: err.response?.data?.message || t('deleteError'),
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const toggleAccountExpansion = (accountId: string) => {
    setExpandedAccounts((prev) => {
      const next = new Set(prev);
      if (next.has(accountId)) {
        next.delete(accountId);
      } else {
        next.add(accountId);
      }
      return next;
    });
  };

  const formatDate = (dateString: string) => {
    const locale = i18n.language === 'fr' ? 'fr-FR' : 'en-US';
    return new Date(dateString).toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatAmount = (amount: number) => {
    const locale = i18n.language === 'fr' ? 'fr-FR' : 'en-US';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const getDaysRemainingColor = (days: number) => {
    if (days <= 3) return 'text-red-600 bg-red-50';
    if (days <= 7) return 'text-orange-600 bg-orange-50';
    return 'text-gray-600 bg-gray-50';
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{error}</p>
        <Button variant="secondary" className="mt-4" onClick={fetchArchives}>
          {t('retry')}
        </Button>
      </div>
    );
  }

  const isEmpty = data && data.stats.totalArchived === 0;

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl">
      <div data-tutorial="archives-page">
        <h1 className="text-2xl font-bold text-gray-900">{t('title')}</h1>
        <p className="text-gray-600">
          {t('description')}
        </p>
      </div>

      {message && (
        <div
          className={`p-4 rounded-lg border ${
            message.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-700'
              : message.type === 'warning'
              ? 'bg-yellow-50 border-yellow-200 text-yellow-700'
              : 'bg-red-50 border-red-200 text-red-700'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Info RGPD */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-blue-800">
          <p className="font-medium">{t('retention.title')}</p>
          <p className="mt-1">
            {t('retention.description')}
          </p>
        </div>
      </div>

      {/* Statistiques */}
      {data && data.stats.totalArchived > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-2xl font-bold text-gray-900">{data.stats.totalArchived}</div>
            <div className="text-sm text-gray-500">{t('stats.totalArchived')}</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-2xl font-bold text-gray-900">{data.stats.byType.bankAccounts}</div>
            <div className="text-sm text-gray-500">{t('stats.accounts')}</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-2xl font-bold text-gray-900">
              {data.stats.byType.personalOperations + data.stats.byType.sharedOperations}
            </div>
            <div className="text-sm text-gray-500">{t('stats.operations')}</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-2xl font-bold text-gray-900">
              {data.stats.byType.budgets + data.stats.byType.envelopes}
            </div>
            <div className="text-sm text-gray-500">{t('stats.budgetsAndEnvelopes')}</div>
          </div>
          {data.stats.byType.categories > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="text-2xl font-bold text-gray-900">{data.stats.byType.categories}</div>
              <div className="text-sm text-gray-500">{t('stats.categories')}</div>
            </div>
          )}
          {data.stats.expiringWithin7Days > 0 && (
            <div className="bg-orange-50 rounded-lg border border-orange-200 p-4">
              <div className="text-2xl font-bold text-orange-600">
                {data.stats.expiringWithin7Days}
              </div>
              <div className="text-sm text-orange-600">{t('stats.expiringSoon')}</div>
            </div>
          )}
        </div>
      )}

      {isEmpty ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Trash2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('empty.title')}</h2>
          <p className="text-gray-500">
            {t('empty.description')}
          </p>
        </div>
      ) : (
        <>
          {/* Onglets */}
          <ScrollableTabs>
            <button
              className={`px-4 py-2 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                activeTab === 'bank-accounts'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('bank-accounts')}
            >
              <CreditCard className="w-4 h-4" />
              {t('tabs.bankAccounts')}
              {data && data.bankAccounts.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                  {data.bankAccounts.length}
                </span>
              )}
            </button>
            <button
              className={`px-4 py-2 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                activeTab === 'personal-operations'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('personal-operations')}
            >
              <Receipt className="w-4 h-4" />
              {t('tabs.personalOperations')}
              {data && data.personalOperations.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                  {data.personalOperations.length}
                </span>
              )}
            </button>
            {hasCouple && (
              <button
                className={`px-4 py-2 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'shared-operations'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('shared-operations')}
              >
                <Users className="w-4 h-4" />
                {t('tabs.sharedOperations')}
                {data && data.sharedOperations.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                    {data.sharedOperations.length}
                  </span>
                )}
              </button>
            )}
            {hasCouple && (
              <button
                className={`px-4 py-2 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'shared-bank-accounts'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('shared-bank-accounts')}
              >
                <Users className="w-4 h-4" />
                {t('tabs.sharedBankAccounts')}
                {data && data.sharedBankAccounts && data.sharedBankAccounts.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                    {data.sharedBankAccounts.length}
                  </span>
                )}
              </button>
            )}
            <button
              className={`px-4 py-2 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                activeTab === 'budgets'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('budgets')}
            >
              <Target className="w-4 h-4" />
              {t('tabs.budgets')}
              {data && data.budgets && data.budgets.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                  {data.budgets.length}
                </span>
              )}
            </button>
            <button
              className={`px-4 py-2 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                activeTab === 'envelopes'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('envelopes')}
            >
              <Wallet className="w-4 h-4" />
              {t('tabs.envelopes')}
              {data && data.envelopes && data.envelopes.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                  {data.envelopes.length}
                </span>
              )}
            </button>
            <button
              className={`px-4 py-2 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                activeTab === 'categories'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('categories')}
            >
              <Tag className="w-4 h-4" />
              {t('tabs.categories')}
              {data && data.categories && data.categories.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                  {data.categories.length}
                </span>
              )}
            </button>
            {hasCouple && (
              <button
                className={`px-4 py-2 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'partner2-incomes'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('partner2-incomes')}
              >
                <TrendingUp className="w-4 h-4" />
                {t('tabs.partner2Incomes')}
                {data && data.partner2Incomes && data.partner2Incomes.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                    {data.partner2Incomes.length}
                  </span>
                )}
              </button>
            )}
          </ScrollableTabs>

          {/* Contenu des onglets */}
          <div className="space-y-4">
            {/* Comptes bancaires */}
            {activeTab === 'bank-accounts' && (
              <>
                {data?.bankAccounts.length === 0 ? (
                  <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                    <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">{t('emptyTabs.bankAccounts')}</p>
                  </div>
                ) : (
                  data?.bankAccounts.map((account) => (
                    <div
                      key={account.id}
                      className="bg-white rounded-lg border border-gray-200 overflow-hidden"
                    >
                      <div className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div
                              className="w-10 h-10 rounded-full flex items-center justify-center"
                              style={{ backgroundColor: account.color + '20' }}
                            >
                              <CreditCard className="w-5 h-5" style={{ color: account.color }} />
                            </div>
                            <div>
                              <h3 className="font-medium text-gray-900">{account.name}</h3>
                              <p className="text-sm text-gray-500">
                                {t('item.archivedOperations', { count: account.operationsCount })}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span
                              className={`px-2 py-1 text-xs rounded-full flex items-center gap-1 ${getDaysRemainingColor(
                                account.daysRemaining
                              )}`}
                            >
                              <Clock className="w-3 h-3" />
                              {t('item.daysRemaining', { count: account.daysRemaining })}
                            </span>
                            {account.operationsCount > 0 && (
                              <button
                                onClick={() => toggleAccountExpansion(account.id)}
                                className="p-1 hover:bg-gray-100 rounded"
                              >
                                {expandedAccounts.has(account.id) ? (
                                  <ChevronUp className="w-5 h-5 text-gray-400" />
                                ) : (
                                  <ChevronDown className="w-5 h-5 text-gray-400" />
                                )}
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="flex gap-2 mt-4">
                          {account.canRestore && (
                            <Button
                              variant="primary"
                              size="sm"
                              leftIcon={<RotateCcw className="w-4 h-4" />}
                              onClick={() => {
                                setSelectedItem({ type: 'bank-accounts', item: account });
                                setShowRestoreModal(true);
                              }}
                            >
                              {t('actions.restore')}
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            leftIcon={<Trash2 className="w-4 h-4" />}
                            onClick={() => {
                              setSelectedItem({ type: 'bank-accounts', item: account });
                              setShowDeleteModal(true);
                            }}
                          >
                            {t('actions.deletePermanently')}
                          </Button>
                        </div>
                      </div>

                      {/* Détails du compte expandé */}
                      {expandedAccounts.has(account.id) && (
                        <div className="border-t border-gray-200 bg-gray-50 p-4">
                          <p className="text-sm text-gray-600 mb-2">{t('item.details')} :</p>
                          <ul className="text-sm text-gray-500 space-y-1">
                            <li>{t('item.archivedOn')} : {formatDate(account.archivedAt)}</li>
                            <li>{t('item.expiresOn')} : {formatDate(account.expiresAt)}</li>
                            {account.startingBalance !== null && (
                              <li>{t('item.startingBalance')} : {formatAmount(account.startingBalance)}</li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </>
            )}

            {/* Opérations personnelles */}
            {activeTab === 'personal-operations' && (
              <>
                {data?.personalOperations.length === 0 ? (
                  <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                    <Receipt className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">{t('emptyTabs.personalOperations')}</p>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
                    {data?.personalOperations.map((op) => (
                      <div key={op.id} className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                op.operationType === 'credit' ? 'bg-green-100' : 'bg-red-100'
                              }`}
                            >
                              <Receipt
                                className={`w-5 h-5 ${
                                  op.operationType === 'credit' ? 'text-green-600' : 'text-red-600'
                                }`}
                              />
                            </div>
                            <div>
                              <h3 className="font-medium text-gray-900">
                                {op.customLabel || op.category?.name || t('item.operation')}
                              </h3>
                              <p className="text-sm text-gray-500">
                                {formatDate(op.operationDate)}
                                {op.bankAccount && ` • ${op.bankAccount.name}`}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p
                              className={`font-medium ${
                                op.operationType === 'credit' ? 'text-green-600' : 'text-red-600'
                              }`}
                            >
                              {op.operationType === 'credit' ? '+' : '-'}
                              {formatAmount(op.amount)}
                            </p>
                            <span
                              className={`px-2 py-0.5 text-xs rounded-full ${getDaysRemainingColor(
                                op.daysRemaining
                              )}`}
                            >
                              {t('item.daysRemaining', { count: op.daysRemaining })}
                            </span>
                          </div>
                        </div>

                        {op.archivedWithAccount && (
                          <p className="mt-2 text-xs text-gray-500 flex items-center gap-1">
                            <Info className="w-3 h-3" />
                            {t('item.archivedWithAccount')}
                          </p>
                        )}

                        <div className="flex gap-2 mt-3">
                          {op.canRestore && !op.archivedWithAccount && (
                            <Button
                              variant="primary"
                              size="sm"
                              leftIcon={<RotateCcw className="w-4 h-4" />}
                              onClick={() => {
                                setSelectedItem({ type: 'personal-operations', item: op });
                                setShowRestoreModal(true);
                              }}
                            >
                              {t('actions.restore')}
                            </Button>
                          )}
                          {!op.archivedWithAccount && (
                            <Button
                              variant="outline"
                              size="sm"
                              leftIcon={<Trash2 className="w-4 h-4" />}
                              onClick={() => {
                                setSelectedItem({ type: 'personal-operations', item: op });
                                setShowDeleteModal(true);
                              }}
                            >
                              {tc('delete')}
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Opérations partagées */}
            {activeTab === 'shared-operations' && hasCouple && (
              <>
                {data?.sharedOperations.length === 0 ? (
                  <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                    <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">{t('emptyTabs.sharedOperations')}</p>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
                    {data?.sharedOperations.map((op) => (
                      <div key={op.id} className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                op.operationType === 'credit' ? 'bg-green-100' : 'bg-red-100'
                              }`}
                            >
                              <Users
                                className={`w-5 h-5 ${
                                  op.operationType === 'credit' ? 'text-green-600' : 'text-red-600'
                                }`}
                              />
                            </div>
                            <div>
                              <h3 className="font-medium text-gray-900">
                                {op.customLabel || op.category?.name || t('item.operation')}
                              </h3>
                              <p className="text-sm text-gray-500">{formatDate(op.operationDate)}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p
                              className={`font-medium ${
                                op.operationType === 'credit' ? 'text-green-600' : 'text-red-600'
                              }`}
                            >
                              {op.operationType === 'credit' ? '+' : '-'}
                              {formatAmount(op.amount)}
                            </p>
                            <span
                              className={`px-2 py-0.5 text-xs rounded-full ${getDaysRemainingColor(
                                op.daysRemaining
                              )}`}
                            >
                              {t('item.daysRemaining', { count: op.daysRemaining })}
                            </span>
                          </div>
                        </div>

                        <div className="flex gap-2 mt-3">
                          {op.canRestore && (
                            <Button
                              variant="primary"
                              size="sm"
                              leftIcon={<RotateCcw className="w-4 h-4" />}
                              onClick={() => {
                                setSelectedItem({ type: 'shared-operations', item: op });
                                setShowRestoreModal(true);
                              }}
                            >
                              {t('actions.restore')}
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            leftIcon={<Trash2 className="w-4 h-4" />}
                            onClick={() => {
                              setSelectedItem({ type: 'shared-operations', item: op });
                              setShowDeleteModal(true);
                            }}
                          >
                            Supprimer
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Comptes communs archivés */}
            {activeTab === 'shared-bank-accounts' && hasCouple && (
              <>
                {!data?.sharedBankAccounts || data.sharedBankAccounts.length === 0 ? (
                  <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                    <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">{t('emptyTabs.sharedBankAccounts')}</p>
                  </div>
                ) : (
                  data.sharedBankAccounts.map((account) => (
                    <div
                      key={account.id}
                      className="bg-white rounded-lg border border-gray-200 overflow-hidden"
                    >
                      <div className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div
                              className="w-10 h-10 rounded-full flex items-center justify-center"
                              style={{ backgroundColor: account.color + '20' }}
                            >
                              <Users className="w-5 h-5" style={{ color: account.color }} />
                            </div>
                            <div>
                              <h3 className="font-medium text-gray-900">{account.name}</h3>
                              <p className="text-sm text-gray-500">
                                {t('item.archivedOperations', { count: account.operationsCount })}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span
                              className={`px-2 py-1 text-xs rounded-full flex items-center gap-1 ${getDaysRemainingColor(
                                account.daysRemaining
                              )}`}
                            >
                              <Clock className="w-3 h-3" />
                              {t('item.daysRemaining', { count: account.daysRemaining })}
                            </span>
                            {account.operationsCount > 0 && (
                              <button
                                onClick={() => toggleAccountExpansion(account.id)}
                                className="p-1 hover:bg-gray-100 rounded"
                              >
                                {expandedAccounts.has(account.id) ? (
                                  <ChevronUp className="w-5 h-5 text-gray-400" />
                                ) : (
                                  <ChevronDown className="w-5 h-5 text-gray-400" />
                                )}
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="flex gap-2 mt-4">
                          {account.canRestore && (
                            <Button
                              variant="primary"
                              size="sm"
                              leftIcon={<RotateCcw className="w-4 h-4" />}
                              onClick={() => {
                                setSelectedItem({ type: 'shared-bank-accounts', item: account });
                                setShowRestoreModal(true);
                              }}
                            >
                              {t('actions.restore')}
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            leftIcon={<Trash2 className="w-4 h-4" />}
                            onClick={() => {
                              setSelectedItem({ type: 'shared-bank-accounts', item: account });
                              setShowDeleteModal(true);
                            }}
                          >
                            {t('actions.deletePermanently')}
                          </Button>
                        </div>
                      </div>

                      {expandedAccounts.has(account.id) && (
                        <div className="border-t border-gray-200 bg-gray-50 p-4">
                          <p className="text-sm text-gray-600 mb-2">{t('item.details')} :</p>
                          <ul className="text-sm text-gray-500 space-y-1">
                            <li>{t('item.archivedOn')} : {formatDate(account.archivedAt)}</li>
                            <li>{t('item.expiresOn')} : {formatDate(account.expiresAt)}</li>
                            {account.startingBalance !== null && (
                              <li>{t('item.startingBalance')} : {formatAmount(account.startingBalance)}</li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </>
            )}

            {/* Budgets prévisionnels */}
            {activeTab === 'budgets' && (
              <>
                {!data?.budgets || data.budgets.length === 0 ? (
                  <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                    <Target className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">{t('emptyTabs.budgets')}</p>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
                    {data.budgets.map((budget) => (
                      <div key={budget.id} className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-indigo-100">
                              {budget.category?.icon ? (
                                <DynamicIcon name={budget.category.icon} className="w-5 h-5 text-indigo-600" />
                              ) : (
                                <Target className="w-5 h-5 text-indigo-600" />
                              )}
                            </div>
                            <div>
                              <h3 className="font-medium text-gray-900">
                                {budget.name || budget.subcategory?.name || budget.category?.name || 'Budget'}
                              </h3>
                              <p className="text-sm text-gray-500">
                                {formatAmount(budget.amount)} / {budget.period === 'monthly' ? t('item.perMonth') : t('item.perWeek')}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-500">
                              {t('item.since', { date: formatDate(budget.startDate) })}
                            </p>
                            <span
                              className={`px-2 py-0.5 text-xs rounded-full ${getDaysRemainingColor(
                                budget.daysRemaining
                              )}`}
                            >
                              {t('item.daysRemaining', { count: budget.daysRemaining })}
                            </span>
                          </div>
                        </div>

                        <div className="flex gap-2 mt-3">
                          {budget.canRestore && (
                            <Button
                              variant="primary"
                              size="sm"
                              leftIcon={<RotateCcw className="w-4 h-4" />}
                              onClick={() => {
                                setSelectedItem({ type: 'budgets', item: budget });
                                setShowRestoreModal(true);
                              }}
                            >
                              {t('actions.restore')}
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            leftIcon={<Trash2 className="w-4 h-4" />}
                            onClick={() => {
                              setSelectedItem({ type: 'budgets', item: budget });
                              setShowDeleteModal(true);
                            }}
                          >
                            Supprimer
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Enveloppes */}
            {activeTab === 'envelopes' && (
              <>
                {!data?.envelopes || data.envelopes.length === 0 ? (
                  <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                    <Wallet className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">{t('emptyTabs.envelopes')}</p>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
                    {data.envelopes.map((envelope) => (
                      <div key={envelope.id} className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-violet-100">
                              <Wallet className="w-5 h-5 text-violet-600" />
                            </div>
                            <div>
                              <h3 className="font-medium text-gray-900">{envelope.name}</h3>
                              <p className="text-sm text-gray-500">
                                {formatAmount(envelope.targetAmount)}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-500">
                              {envelope.status}
                            </p>
                            <span
                              className={`px-2 py-0.5 text-xs rounded-full ${getDaysRemainingColor(
                                envelope.daysRemaining
                              )}`}
                            >
                              {t('item.daysRemaining', { count: envelope.daysRemaining })}
                            </span>
                          </div>
                        </div>

                        {envelope.reattachableOperationsCount > 0 && (
                          <p className="text-xs text-gray-500 mt-2">
                            {t('item.reattachableOperations', { count: envelope.reattachableOperationsCount })}
                          </p>
                        )}

                        <div className="flex gap-2 mt-3">
                          {envelope.canRestore && (
                            <Button
                              variant="primary"
                              size="sm"
                              leftIcon={<RotateCcw className="w-4 h-4" />}
                              onClick={() => {
                                setSelectedItem({ type: 'envelopes', item: envelope });
                                setShowRestoreModal(true);
                              }}
                            >
                              {t('actions.restore')}
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            leftIcon={<Trash2 className="w-4 h-4" />}
                            onClick={() => {
                              setSelectedItem({ type: 'envelopes', item: envelope });
                              setShowDeleteModal(true);
                            }}
                          >
                            {t('actions.deletePermanently')}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Catégories */}
            {activeTab === 'categories' && (
              <>
                {!data?.categories || data.categories.length === 0 ? (
                  <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                    <Tag className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">{t('emptyTabs.categories')}</p>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
                    {data.categories.map((category) => (
                      <div key={category.id} className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-amber-100">
                              {category.icon ? (
                                <DynamicIcon name={category.icon} className="w-5 h-5 text-amber-600" />
                              ) : (
                                <Tag className="w-5 h-5 text-amber-600" />
                              )}
                            </div>
                            <div>
                              <h3 className="font-medium text-gray-900">{category.name}</h3>
                              <p className="text-sm text-gray-500">
                                {t(`categoryKind.${category.categoryKind}`)}
                              </p>
                            </div>
                          </div>
                          <span
                            className={`px-2 py-0.5 text-xs rounded-full ${getDaysRemainingColor(
                              category.daysRemaining
                            )}`}
                          >
                            {t('item.daysRemaining', { count: category.daysRemaining })}
                          </span>
                        </div>

                        <div className="text-xs text-gray-500 mt-2 space-y-0.5">
                          {category.subcategoriesCount > 0 && (
                            <p>{t('item.subcategoriesCount', { count: category.subcategoriesCount })}</p>
                          )}
                          {category.operationsCount > 0 && (
                            <p>{t('item.categoryOperations', { count: category.operationsCount })}</p>
                          )}
                          {category.budgetsCount > 0 && (
                            <p>{t('item.categoryBudgets', { count: category.budgetsCount })}</p>
                          )}
                          {category.reattachableOperationsCount > 0 && (
                            <p className="text-emerald-600">
                              {t('item.reattachableOperations', { count: category.reattachableOperationsCount })}
                            </p>
                          )}
                        </div>

                        <div className="flex gap-2 mt-3">
                          {category.canRestore && (
                            <Button
                              variant="primary"
                              size="sm"
                              leftIcon={<RotateCcw className="w-4 h-4" />}
                              onClick={() => {
                                setSelectedItem({ type: 'categories', item: category });
                                setShowRestoreModal(true);
                              }}
                            >
                              {t('actions.restore')}
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            leftIcon={<Trash2 className="w-4 h-4" />}
                            onClick={() => {
                              setSelectedItem({ type: 'categories', item: category });
                              setShowDeleteModal(true);
                            }}
                          >
                            {t('actions.deletePermanently')}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Revenus du partenaire 2 */}
            {activeTab === 'partner2-incomes' && hasCouple && (
              <>
                {!data?.partner2Incomes || data.partner2Incomes.length === 0 ? (
                  <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                    <TrendingUp className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">{t('emptyTabs.partner2Incomes')}</p>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
                    {data.partner2Incomes.map((income) => (
                      <div key={income.id} className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-sky-100">
                              <TrendingUp className="w-5 h-5 text-sky-600" />
                            </div>
                            <div>
                              <h3 className="font-medium text-gray-900">
                                {income.label || t(income.isOneOff ? 'item.oneOffIncome' : 'item.durableIncome')}
                              </h3>
                              <p className="text-sm text-gray-500">
                                {formatAmount(income.amount)} · {String(income.effectiveMonth).padStart(2, '0')}/{income.effectiveYear}
                              </p>
                            </div>
                          </div>
                          <span
                            className={`px-2 py-0.5 text-xs rounded-full ${getDaysRemainingColor(
                              income.daysRemaining
                            )}`}
                          >
                            {t('item.daysRemaining', { count: income.daysRemaining })}
                          </span>
                        </div>

                        <div className="flex gap-2 mt-3">
                          {income.canRestore && (
                            <Button
                              variant="primary"
                              size="sm"
                              leftIcon={<RotateCcw className="w-4 h-4" />}
                              onClick={() => {
                                setSelectedItem({ type: 'partner2-incomes', item: income });
                                setShowRestoreModal(true);
                              }}
                            >
                              {t('actions.restore')}
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            leftIcon={<Trash2 className="w-4 h-4" />}
                            onClick={() => {
                              setSelectedItem({ type: 'partner2-incomes', item: income });
                              setShowDeleteModal(true);
                            }}
                          >
                            {t('actions.deletePermanently')}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* Modal de restauration */}
      {showRestoreModal && selectedItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-primary-600" />
              {t('restoreModal.title')}
            </h2>
            <p className="text-gray-600 mb-4">
              {t('restoreModal.message')}
            </p>

            {selectedItem.type === 'bank-accounts' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-800">
                  {t('restoreModal.accountNote', { count: (selectedItem.item as ArchivedBankAccount).operationsCount })}
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => {
                  setShowRestoreModal(false);
                  setSelectedItem(null);
                }}
              >
                {tc('cancel')}
              </Button>
              <Button
                variant="primary"
                className="flex-1"
                onClick={handleRestore}
                isLoading={isProcessing}
              >
                {t('actions.restore')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de suppression définitive */}
      {showDeleteModal && selectedItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-red-600 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              {t('deleteModal.title')}
            </h2>
            <p className="text-gray-600 mb-4">
              <Trans t={t} i18nKey="deleteModal.message" components={{ strong: <strong /> }} />
            </p>


            {selectedItem.type === 'bank-accounts' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-red-800">
                  {t('deleteModal.accountNote', { count: (selectedItem.item as ArchivedBankAccount).operationsCount })}
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedItem(null);
                }}
              >
                {tc('cancel')}
              </Button>
              <Button
                variant="danger"
                className="flex-1"
                onClick={handlePermanentDelete}
                isLoading={isProcessing}
              >
                {t('actions.deletePermanently')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
